import React, { useState } from 'react';
import { Check, Close } from '@icon-park/react';
import { useTranslation } from 'react-i18next';

interface FurniturePropertyPanelProps {
  position: { x: number; y: number };
  initialName?: string;
  initialWidth?: number;
  initialDepth?: number;
  initialHeight?: number;
  onConfirm: (data: { name: string; width: number; depth: number; height: number }) => void;
  onCancel: () => void;
}

const FurniturePropertyPanel: React.FC<FurniturePropertyPanelProps> = ({
  position,
  initialName = '',
  initialWidth = 800,
  initialDepth = 600,
  initialHeight = 800,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [furnitureName, setFurnitureName] = useState(initialName);
  const [width, setWidth] = useState(initialWidth);
  const [depth, setDepth] = useState(initialDepth);
  const [height, setHeight] = useState(initialHeight);

  const handleConfirm = () => {
    // 确保尺寸有效
    const validWidth = Math.max(1, width);
    const validDepth = Math.max(1, depth);
    const validHeight = Math.max(1, height);
    onConfirm({ name: furnitureName, width: validWidth, depth: validDepth, height: validHeight });
  };

  return (
    <div
      className="absolute bg-white rounded shadow-lg border border-gray-300 p-2 z-50"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: '180px',
      }}
    >
      {/* 家具名称 */}
      <div className="mb-2">
        <label className="block text-xs text-gray-600 mb-0.5">
          {t('elementProps.furnitureName')}
        </label>
        <input
          type="text"
          value={furnitureName}
          onChange={(e) => setFurnitureName(e.target.value)}
          placeholder={t('elementProps.furniturePlaceholder')}
          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleConfirm();
            } else if (e.key === 'Escape') {
              onCancel();
            }
          }}
        />
      </div>

      {/* 尺寸设置 */}
      <div className="mb-2">
        <label className="block text-xs text-gray-600 mb-0.5">
          {t('elementProps.width')}
        </label>
        <input
          type="number"
          value={width}
          onChange={(e) => setWidth(Math.max(1, Number(e.target.value)))}
          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
          min="1"
          max="5000"
          step="50"
        />
      </div>

      <div className="mb-2">
        <label className="block text-xs text-gray-600 mb-0.5">
          {t('elementProps.depth')}
        </label>
        <input
          type="number"
          value={depth}
          onChange={(e) => setDepth(Math.max(1, Number(e.target.value)))}
          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
          min="1"
          max="5000"
          step="50"
        />
      </div>

      <div className="mb-2">
        <label className="block text-xs text-gray-600 mb-0.5">
          {t('elementProps.height')}
        </label>
        <input
          type="number"
          value={height}
          onChange={(e) => setHeight(Number(e.target.value))}
          className="w-full px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-300"
          min="100"
          max="3000"
          step="50"
        />
      </div>

      {/* 操作按钮 */}
      <div className="flex gap-1">
        <button
          onClick={handleConfirm}
          className="flex-1 flex items-center justify-center gap-0.5 px-1.5 py-0.5 bg-primary-500 text-white text-xs rounded hover:bg-primary-600 transition-colors"
        >
          <Check size={12} theme="outline" />
          <span className="text-[10px]">{t('dialog.confirm')}</span>
        </button>
        <button
          onClick={onCancel}
          className="flex-1 flex items-center justify-center gap-0.5 px-1.5 py-0.5 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 transition-colors"
        >
          <Close size={12} theme="outline" />
          <span className="text-[10px]">{t('dialog.cancel')}</span>
        </button>
      </div>
    </div>
  );
};

export default FurniturePropertyPanel;
